import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Button, Container, makeStyles, MenuItem, TextField, Typography } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import Header from '../../../Header/header';
import Footer from '../../../Footer/footer';
import ContentTitle from '../../../General/contentTitle';
import { AuthContext } from '../../../auth';
import { blogErrorMessage, getBlogSessionToken, getPublicBlogNavigation, getPublicBlogPost, listPublicBlogFilters, listPublicBlogPosts, listRelatedBlogPosts } from '../../../Apis/touzenBlogApi';
import BlogBodyText from '../components/BlogBodyText';
import BlogShareActions from '../components/BlogShareActions';
import BlogLikeButton from '../components/BlogLikeButton';
import BlogComments from '../components/BlogComments';
import BlogSeoHead from '../components/BlogSeoHead';
import { BlogReleaseNotice, BlogReleasePolicyProvider, useBlogReleasePolicy } from '../components/BlogReleasePolicy';


const useStyles = makeStyles((theme) => ({
  main: { marginTop: theme.spacing(18), marginBottom: theme.spacing(12), textAlign: 'left', minHeight: '55vh' },
  blogTitleLink: {
    display: 'block', color: 'inherit', textDecoration: 'none',
    '&:hover': { textDecoration: 'underline', textDecorationColor: theme.palette.primary.main },
    '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 4 },
  },
  list: { listStyle: 'none', padding: 0, margin: 0 },
  row: {
    display: 'flex', alignItems: 'stretch', gap: theme.spacing(3), padding: theme.spacing(4, 0),
    borderBottom: `1px solid ${theme.palette.divider}`, overflowWrap: 'anywhere',
    [theme.breakpoints.down('xs')]: { flexDirection: 'column' },
  },
  coverLink: {
    display: 'block', position: 'relative', flex: '0 0 32%', width: '32%',
    backgroundColor: theme.palette.action.hover,
    '&::before': { content: '""', display: 'block', paddingTop: '75%' },
    [theme.breakpoints.down('xs')]: { width: '100%', flexBasis: 'auto' },
  },
  thumbnail: { display: 'block', position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'contain' },
  rowText: { display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, '& h2': { margin: theme.spacing(1, 0, 0.5) } },
  titleLink: { color: theme.palette.text.primary, textDecoration: 'none', '&:hover, &:focus': { textDecoration: 'underline' } },
  readAction: {
    color: '#6F6F6F',
    fontSize: '0.875rem',
    paddingBottom: 0,
    alignItems: 'flex-end',
    '&:visited': { color: '#6F6F6F' },
    '&:hover, &:focus': { color: theme.palette.primary.main },
  },
  excerpt: { fontSize: '1rem', lineHeight: 1.8 },
  actions: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(2), margin: theme.spacing(3, 0), '& a, & button': { minHeight: 44 } },
  readActions: { marginTop: 'auto', marginBottom: 0, paddingTop: theme.spacing(3) },
  reactions: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: theme.spacing(1) },
  commentSummary: { display: 'inline-flex', alignItems: 'center', gap: theme.spacing(1), minHeight: 44, padding: theme.spacing(1), color: '#6F6F6F', fontSize: '0.875rem' },
  commentIcon: { fontSize: '20px' },
  meta: { color: theme.palette.text.secondary, fontSize: '0.875rem', lineHeight: 1.7 },
  metaLink: { color: 'inherit', textDecoration: 'underline', '&:hover, &:focus': { color: theme.palette.primary.main } },
  filterBox: { display: 'grid', gap: theme.spacing(2), marginBottom: theme.spacing(4), padding: theme.spacing(2, 0), fontSize: '0.875rem', borderTop: `1px solid ${theme.palette.divider}`, borderBottom: `1px solid ${theme.palette.divider}` },
  tagLinks: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: theme.spacing(1), minWidth: 0 },
  tagLink: {
    display: 'inline-flex', alignItems: 'center', minHeight: 44, maxWidth: '100%', padding: '0 4px',
    fontSize: '0.875rem', fontWeight: 700, fontStyle: 'italic', overflowWrap: 'anywhere', color: '#6F6F6F', textDecoration: 'none',
    '&:hover, &:focus': { color: theme.palette.primary.main },
    '&[aria-current="true"]': { color: theme.palette.primary.main, textDecoration: 'underline' },
    '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
  },
  postTags: {
    marginBottom: theme.spacing(1),
    '& $tagLink': { minHeight: 32, fontSize: '0.8125rem', fontWeight: 400 },
    [theme.breakpoints.down('xs')]: { '& $tagLink': { minHeight: 44 } },
  },
  filterControls: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: theme.spacing(1, 2), '& button': { fontSize: '0.875rem', minHeight: 44 } },
  filterClear: { color: '#6F6F6F', '&:hover, &:focus': { color: theme.palette.primary.main } },
  authorField: { width: 240, maxWidth: '100%' },
  authorLabel: { fontFamily: theme.typography.fontFamily, fontSize: '14px', '&.MuiInputLabel-shrink': { transform: 'translate(14px, -9px) scale(1)' } },
  authorInput: {
    minHeight: 44, fontFamily: theme.typography.fontFamily, fontSize: '16px', borderRadius: 8,
    boxShadow: '0px 0px 15px 0px rgba(0,0,0,0.1) inset',
    '&:hover $authorOutline, &.Mui-focused $authorOutline': { borderColor: theme.palette.primary.main },
  },
  authorOutline: { borderColor: 'grey', borderWidth: '0.5px', '& legend': { fontSize: '14px' } },
  authorValue: { fontFamily: theme.typography.fontFamily, fontSize: '16px', lineHeight: 1.5, padding: '10px 32px 10px 14px' },
  authorMenu: { fontFamily: theme.typography.fontFamily, fontSize: '16px', borderRadius: 8, border: '0.5px solid grey' },
  authorOption: {
    minHeight: 44, fontFamily: theme.typography.fontFamily, fontSize: '16px', lineHeight: 1.5, whiteSpace: 'normal', overflowWrap: 'anywhere',
    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) },
    '&.Mui-selected, &.Mui-selected:hover, &.Mui-focusVisible': { color: theme.palette.primary.main, backgroundColor: alpha(theme.palette.primary.main, 0.08) },
  },
  detailCover: { display: 'block', width: 'auto', height: 'auto', maxWidth: 'min(100%, 640px)', maxHeight: 'min(400px, 60vh)', objectFit: 'contain', margin: theme.spacing(3, 'auto', 4) },
  detailTitle: { margin: theme.spacing(2, 0, 0.5), overflowWrap: 'anywhere' },
  detailReturn: {
    display: 'inline-flex', alignItems: 'center', gap: '4px', minHeight: 44, fontSize: '0.875rem',
    color: '#6F6F6F', textDecoration: 'none',
    '&:visited': { color: '#6F6F6F' },
    '&:hover, &:focus': { color: theme.palette.primary.main },
    '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
  },
  returnIcon: { fontSize: '14px', flexShrink: 0 },
  adjacent: {
    display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: theme.spacing(2), marginTop: theme.spacing(4),
    [theme.breakpoints.down('xs')]: { gridTemplateColumns: 'minmax(0, 1fr)' },
  },
  adjacentLink: {
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: theme.spacing(0.5), minWidth: 0, minHeight: 44, padding: theme.spacing(1),
    color: '#6F6F6F', fontSize: '0.875rem', textDecoration: 'none', overflowWrap: 'anywhere',
    '&:visited': { color: '#6F6F6F' }, '&:hover, &:focus': { color: theme.palette.primary.main },
    '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
  },
  adjacentLabel: { display: 'inline-flex', alignItems: 'center', gap: theme.spacing(0.5) },
  adjacentNext: { gridColumn: 2, alignItems: 'flex-end', textAlign: 'right', [theme.breakpoints.down('xs')]: { gridColumn: 'auto', alignItems: 'flex-start', textAlign: 'left' } },
  related: { marginTop: theme.spacing(4), padding: theme.spacing(2, 0), borderTop: `1px solid ${theme.palette.divider}`, borderBottom: `1px solid ${theme.palette.divider}` },
  relatedTrack: {
    display: 'grid', gridAutoFlow: 'column', gridAutoColumns: 'minmax(220px, 280px)', gap: theme.spacing(2),
    minWidth: 0, maxWidth: '100%', overflowX: 'auto', padding: theme.spacing(1), scrollPadding: '8px', scrollSnapType: 'x mandatory',
  },
  relatedCard: {
    display: 'flex', flexDirection: 'column', gap: theme.spacing(1), minWidth: 0, padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`, borderRadius: theme.shape.borderRadius, backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary, textDecoration: 'none', scrollSnapAlign: 'start', overflowWrap: 'anywhere',
    '&:hover, &:focus': { color: theme.palette.primary.main },
    '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
  },
  relatedImage: { display: 'block', width: '100%', aspectRatio: '4 / 3', objectFit: 'contain', backgroundColor: theme.palette.action.hover },
  relatedTitle: { fontSize: '1rem', fontWeight: 700, lineHeight: 1.5 },
  relatedMeta: { fontSize: '0.75rem', color: theme.palette.text.secondary },
  relatedExcerpt: { fontSize: '0.875rem', lineHeight: 1.7 },
  pagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: theme.spacing(2), marginTop: theme.spacing(5) },
  pageNumbers: { display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: theme.spacing(1) },
  pageButton: {
    minWidth: 44, minHeight: 44, padding: theme.spacing(1), border: 0, background: 'transparent',
    color: '#6F6F6F', fontFamily: 'inherit', fontSize: '1rem', fontWeight: 700, cursor: 'pointer',
    '&:hover, &:focus': { color: theme.palette.primary.main },
    '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
    '&:disabled': { opacity: 0.35, cursor: 'default' },
    '&[aria-current="page"]': { textDecoration: 'underline', textUnderlineOffset: '0.18em' },
  },
}));

const pageNumber = (raw) => /^\d+$/.test(raw || '') && Number.isSafeInteger(Number(raw)) && Number(raw) > 0 ? Number(raw) : 1;
function pageQuery(searchParams, page) {
  const next = new URLSearchParams(searchParams);
  if (page === 1) next.delete('page');
  else next.set('page', String(page));
  const query = next.toString();
  return query ? `?${query}` : '';
}
function visiblePages(page, total) {
  const numbers = total <= 7 ? Array.from({ length: total }, (_, index) => index + 1)
    : [...new Set([1, page - 1, page, page + 1, total])].filter((number) => number > 0 && number <= total).sort((a, b) => a - b);
  return numbers.flatMap((number, index) => index && number > numbers[index - 1] + 1 ? [`gap-${number}`, number] : [number]);
}
function listPreview(post) {
  const text = post.excerpt || post.body_preview;
  const characters = Array.from(text);
  return characters.length > 90 ? `${characters.slice(0, 90).join('')}…` : text;
}
function changedFilters(searchParams, changes) {
  const next = new URLSearchParams(searchParams);
  Object.entries(changes).forEach(([field, value]) => {
    if (value === null || value === '') next.delete(field);
    else next.set(field, String(value));
  });
  next.delete('page');
  return next;
}
function filterHref(searchParams, changes) {
  const query = changedFilters(searchParams, changes).toString();
  return `/blog${query ? `?${query}` : ''}`;
}
function PostTags({ post, searchParams }) {
  const classes = useStyles();
  if (!post.tags?.length) return null;
  return <div role="group" aria-label="記事のタグ" className={`${classes.tagLinks} ${classes.postTags}`}>{post.tags.map((tag) => <Link key={tag.id}
    className={classes.tagLink} to={filterHref(searchParams, { tag: tag.id })}>{tag.name}</Link>)}</div>;
}
function PostAuthor({ post, searchParams }) {
  const classes = useStyles();
  return <Typography className={classes.meta}>投稿者：{post.author_key && post.author_display_name?.trim()
    ? <Link className={classes.metaLink} to={filterHref(searchParams, { author: post.author_key })}>{post.author_display_name}</Link>
    : post.author_display_name || '未設定'}</Typography>;
}
function PublicFilters({ searchParams, setSearchParams, authScope, isAuthenticated }) {
  const classes = useStyles();
  const token = isAuthenticated ? getBlogSessionToken() : '';
  const [state, setState] = useState({ scope: authScope, loading: true, tags: [], authors: [], error: null });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setState({ scope: authScope, loading: true, tags: [], authors: [], error: null });
    const request = isAuthenticated ? listPublicBlogFilters(controller.signal, { authenticated: true, expectedToken: token }) : listPublicBlogFilters(controller.signal);
    request.then((data) => {
      if (active) setState({ ...data, scope: authScope, loading: false, error: null });
    }).catch((error) => {
      if (active) setState({ scope: authScope, loading: false, tags: [], authors: [], error });
    });
    return () => { active = false; controller.abort(); };
  }, [authScope, attempt, isAuthenticated, token]);
  const current = state.scope === authScope;
  const tags = current ? state.tags : [];
  const authors = current ? state.authors : [];
  const selectedTag = searchParams.get('tag');
  const selectedAuthor = (searchParams.get('author') || '').toLowerCase();
  const unknownAuthor = selectedAuthor && !authors.some((author) => author.key === selectedAuthor);
  const active = searchParams.has('tag') || searchParams.has('author');
  return <section aria-label="記事の絞り込み" className={classes.filterBox}>
    {(!current || state.loading) && <Typography role="status" className={classes.relatedExcerpt}>絞り込み候補を読み込んでいます。</Typography>}
    {current && state.error && <Typography role="alert" className={classes.relatedExcerpt}>絞り込み候補を取得できませんでした。{blogErrorMessage(state.error)}</Typography>}
    <nav aria-label="タグで絞り込み" className={classes.tagLinks}>
      <Link className={classes.tagLink} aria-current={!searchParams.has('tag') ? 'true' : undefined} to={filterHref(searchParams, { tag: null })}>すべてのタグ</Link>
      {tags.map((tag) => <Link key={tag.id} className={classes.tagLink} aria-current={selectedTag === String(tag.id) ? 'true' : undefined}
        to={filterHref(searchParams, { tag: tag.id })}>{tag.name}</Link>)}
      {searchParams.has('tag') && !tags.some((tag) => String(tag.id) === selectedTag)
        && <Link className={classes.tagLink} to={filterHref(searchParams, { tag: null })}>選択中のタグを解除</Link>}
    </nav>
    <div className={classes.filterControls}>
      <TextField id="blog-author-filter" select label="投稿者" variant="outlined" size="small" className={classes.authorField} value={selectedAuthor}
        InputLabelProps={{ shrink: true, className: classes.authorLabel }}
        InputProps={{ classes: { root: classes.authorInput, notchedOutline: classes.authorOutline } }}
        SelectProps={{ displayEmpty: true, classes: { select: classes.authorValue }, MenuProps: { PaperProps: { className: classes.authorMenu } } }}
        onChange={(event) => setSearchParams(changedFilters(searchParams, { author: event.target.value || null }))}>
        <MenuItem className={classes.authorOption} value="">すべての投稿者</MenuItem>
        {unknownAuthor && <MenuItem className={classes.authorOption} value={selectedAuthor}>選択中の投稿者（候補外）</MenuItem>}
        {authors.map((author) => <MenuItem className={classes.authorOption} key={author.key} value={author.key}>{author.name}</MenuItem>)}
      </TextField>
      <Button type="button" className={classes.filterClear} onClick={() => setSearchParams(changedFilters(searchParams, { tag: null, author: null }))} disabled={!active}>すべて解除</Button>
      {current && state.error && <Button type="button" onClick={() => setAttempt((value) => value + 1)}>絞り込み候補を再読み込み</Button>}
    </div>
  </section>;
}

function AdjacentPosts({ slug, returnQuery, authScope, isAuthenticated, filters }) {
  const classes = useStyles();
  const token = isAuthenticated ? getBlogSessionToken() : '';
  const scope = JSON.stringify([slug, authScope, token, filters]);
  const [state, setState] = useState({ scope, loading: true, previous: null, next: null, error: null });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setState({ scope, loading: true, previous: null, next: null, error: null });
    getPublicBlogNavigation(slug, controller.signal, filters, { authenticated: isAuthenticated, expectedToken: token }).then((data) => {
      if (active) setState({ ...data, scope, loading: false, error: null });
    }).catch((error) => {
      if (active) setState({ scope, loading: false, previous: null, next: null, error });
    });
    return () => { active = false; controller.abort(); };
  }, [slug, scope, filters, isAuthenticated, token, attempt]);
  if (state.scope !== scope || state.loading) return <Typography role="status" className={classes.meta}>前後の記事を読み込んでいます。</Typography>;
  if (state.error) return <section aria-label="前後の記事">
    <Typography role="alert" className={classes.meta}>{state.error.kind === 'not_found' ? '現在の絞り込み条件では前後の記事を表示できません。'
      : `前後の記事を取得できませんでした。${blogErrorMessage(state.error)}`}</Typography>
    <Button onClick={() => setAttempt((value) => value + 1)}>前後の記事を再読み込み</Button>
  </section>;
  if (!state.previous && !state.next) return null;
  return <nav aria-label="前後の記事" className={classes.adjacent}>
    {state.previous && <Link className={classes.adjacentLink} rel="prev" to={`/blog/posts/${encodeURIComponent(state.previous.slug)}${returnQuery}`}>
      <span className={classes.adjacentLabel}><ArrowBackIcon className={classes.returnIcon} aria-hidden="true" focusable="false" />前の記事</span><span>{state.previous.title}</span>
    </Link>}
    {state.next && <Link className={`${classes.adjacentLink} ${classes.adjacentNext}`} rel="next" to={`/blog/posts/${encodeURIComponent(state.next.slug)}${returnQuery}`}>
      <span className={classes.adjacentLabel}>次の記事<ArrowForwardIcon className={classes.returnIcon} aria-hidden="true" focusable="false" /></span><span>{state.next.title}</span>
    </Link>}
  </nav>;
}

function RelatedPosts({ slug, returnQuery, authScope }) {
  const classes = useStyles();
  const scope = `${slug}:${authScope}`;
  const [state, setState] = useState({ scope, loading: true, posts: [], error: null });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setState({ scope, loading: true, posts: [], error: null });
    listRelatedBlogPosts(slug, controller.signal).then((posts) => {
      if (active) setState({ scope, loading: false, posts, error: null });
    }).catch((error) => {
      if (active) setState({ scope, loading: false, posts: [], error });
    });
    return () => { active = false; controller.abort(); };
  }, [slug, scope, attempt]);
  if (state.scope !== scope || state.loading) return <Typography role="status" variant="body2" className={classes.relatedExcerpt}>関連コンテンツを読み込んでいます。</Typography>;
  if (!state.error && state.posts.length === 0) return null;
  return <section className={classes.related} aria-label="関連コンテンツ">
    {state.error ? <>
      <Typography role="alert" variant="body2" className={classes.relatedExcerpt}>記事本文は表示していますが、関連コンテンツを取得できませんでした。{blogErrorMessage(state.error)}</Typography>
      <Button onClick={() => setAttempt((value) => value + 1)}>関連記事を再読み込み</Button>
    </> : <div className={classes.relatedTrack}>{state.posts.map((post) => {
      const text = Array.from(post.excerpt || post.body_preview);
      return <Link key={post.slug} className={classes.relatedCard} to={`/blog/posts/${encodeURIComponent(post.slug)}${returnQuery}`}>
        <Typography component="h2" className={classes.relatedTitle}>{post.title}</Typography>
        <Typography className={classes.relatedMeta}>投稿者：{post.author_display_name || '未設定'}</Typography>
        <Typography className={classes.relatedMeta}><BlogDate label="公開日" value={post.published_at} /></Typography>
        {post.cover_image_url && <img className={classes.relatedImage} src={post.cover_image_url} alt={`${post.title}の代表画像`} loading="lazy" />}
        <Typography className={classes.relatedExcerpt}>{text.length > 72 ? `${text.slice(0, 72).join('')}…` : text.join('')}</Typography>
      </Link>;
    })}</div>}
  </section>;
}

export function BlogDate({ value, label }) {
  const date = value ? new Date(value) : null;
  return <span>{label}：{date && !Number.isNaN(date.getTime())
    ? <time dateTime={value}>{date.toLocaleDateString('ja-JP')}</time> : '未設定'}</span>;
}

function PostDates({ post }) {
  const classes = useStyles();
  const edited = typeof post.published_at === 'string' && Number.isFinite(Date.parse(post.published_at))
    && typeof post.public_edited_at === 'string' && Number.isFinite(Date.parse(post.public_edited_at));
  return <>
    <Typography className={classes.meta}><BlogDate label="公開日" value={post.published_at} /></Typography>
    {edited && <Typography className={classes.meta}><BlogDate label="更新日" value={post.public_edited_at} /></Typography>}
  </>;
}

export function BlogLayout({ children, manage = false }) {
  const classes = useStyles();
  return <BlogReleasePolicyProvider>
    <Header logo={true} icons={true} color="General" />
    <Container component="main" maxWidth="md" className={classes.main}>
      <BlogReleaseNotice />
      {manage ? <ContentTitle title={{ titleList: ['Blog'], sub: 'ブログ管理' }} />
        : <Link className={classes.blogTitleLink} to="/blog" aria-label="Blog 刀禅ブログ"><ContentTitle title={{ titleList: ['Blog'], sub: '刀禅ブログ' }} /></Link>}
      {children}
    </Container>
    <Footer />
  </BlogReleasePolicyProvider>;
}

// Shared with the authenticated preview so saved content uses the public typography.
export function BlogPostArticle({ post, searchParams = new URLSearchParams(), beforeBody = null, afterBody = null }) {
  const classes = useStyles();
  return <article>
    {post.cover_image_url && <img className={classes.detailCover} src={post.cover_image_url} alt={`${post.title}の代表画像`} />}
    <PostAuthor post={post} searchParams={searchParams} />
    <PostDates post={post} />
    <Typography component="h1" variant="h4" className={classes.detailTitle}>{post.title}</Typography>
    <PostTags post={post} searchParams={searchParams} />
    {beforeBody}
    <BlogBodyText text={post.body} />
    {afterBody}
  </article>;
}

export function BlogPagination({ page, totalPages, onPageChange, label = 'ブログ一覧のページ送り' }) {
  const classes = useStyles();
  if (!Number.isSafeInteger(totalPages) || totalPages <= 1) return null;
  return <nav className={classes.pagination} aria-label={label}>
    <button type="button" className={classes.pageButton} onClick={() => onPageChange(page - 1)} disabled={page === 1}>前へ</button>
    <div className={classes.pageNumbers}>{visiblePages(page, totalPages).map((number) => typeof number === 'number'
      ? <button key={number} type="button" className={classes.pageButton} aria-current={number === page ? 'page' : undefined} onClick={() => onPageChange(number)}>{number}</button>
      : <span key={number} aria-hidden="true">…</span>)}</div>
    <button type="button" className={classes.pageButton} onClick={() => onPageChange(page + 1)} disabled={page === totalPages}>次へ</button>
  </nav>;
}

function PublicContent({ slug, authScope, isAuthenticated }) {
  const classes = useStyles();
  const policy = useBlogReleasePolicy();
  const listAllowed = policy.public_release || policy.can_manage;
  const requestAllowed = Boolean(slug) || listAllowed;
  const authenticatedList = !slug && isAuthenticated;
  const listToken = authenticatedList ? getBlogSessionToken() : '';
  const [searchParams, setSearchParams] = useSearchParams();
  const page = pageNumber(searchParams.get('page'));
  const selectedTag = searchParams.get('tag');
  const selectedAuthor = searchParams.get('author');
  const duplicateTag = searchParams.getAll('tag').length > 1;
  const duplicateAuthor = searchParams.getAll('author').length > 1;
  const filters = useMemo(() => ({
    ...(selectedTag !== null ? { tag: !duplicateTag && /^[1-9]\d*$/.test(selectedTag) && Number.isSafeInteger(Number(selectedTag)) ? Number(selectedTag) : NaN } : {}),
    ...(selectedAuthor !== null ? { author: duplicateAuthor ? null : selectedAuthor } : {}),
  }), [selectedTag, selectedAuthor, duplicateTag, duplicateAuthor]);
  const requestKey = JSON.stringify([page, selectedTag, selectedAuthor, duplicateTag, duplicateAuthor]);
  const [state, setState] = useState({ loading: true, posts: [], post: null, pagination: null, requestKey, error: null });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setState({ loading: true, posts: [], post: null, pagination: null, requestKey, error: null });
    if (!requestAllowed) return () => { active = false; controller.abort(); };
    (slug ? getPublicBlogPost(slug, controller.signal) : authenticatedList
      ? listPublicBlogPosts(controller.signal, page, filters, { authenticated: true, expectedToken: listToken })
      : listPublicBlogPosts(controller.signal, page, filters))
      .then((data) => {
        if (active) setState({ loading: false, posts: slug ? [] : data.results, post: slug ? data : null,
          pagination: slug ? null : data, requestKey, error: null });
      }).catch((error) => {
        if (active) setState({ loading: false, posts: [], post: null, pagination: null, requestKey, error });
      });
    return () => { active = false; controller.abort(); };
  }, [slug, page, filters, requestKey, attempt, requestAllowed, authenticatedList, listToken]);

  useEffect(() => {
    if (slug) return;
    const normalized = state.requestKey === requestKey && state.pagination ? state.pagination.page : page;
    const query = pageQuery(searchParams, normalized);
    if (query !== (searchParams.toString() ? `?${searchParams.toString()}` : '')) {
      setSearchParams(new URLSearchParams(query), { replace: true });
    }
  }, [slug, page, searchParams, setSearchParams, state.pagination, state.requestKey, requestKey]);

  const loading = state.loading || state.requestKey !== requestKey;
  const currentPage = state.pagination?.page || page;
  const returnQuery = pageQuery(searchParams, slug ? page : currentPage);
  const articleHref = (post) => `/blog/posts/${encodeURIComponent(post.slug)}${returnQuery}`;
  const changePage = (nextPage) => {
    if (nextPage === currentPage) return;
    setSearchParams(new URLSearchParams(pageQuery(searchParams, nextPage)));
  };

  return <>
    <BlogSeoHead ready={!loading && !state.error} />
    {!slug && !listAllowed && <Typography role="status">{policy.loading ? '公開設定を確認しています。' : 'ブログの一般公開前です。'}</Typography>}
    {!slug && listAllowed && <PublicFilters searchParams={searchParams} setSearchParams={setSearchParams} authScope={authScope} isAuthenticated={isAuthenticated} />}
    {loading && (slug || listAllowed) && <Typography role="status">記事を読み込んでいます。</Typography>}
    {!loading && state.error && (slug || listAllowed) && <div role="alert">
      <Typography>{blogErrorMessage(state.error)}</Typography>
      <Button onClick={() => setAttempt((value) => value + 1)}>再読み込み</Button>
    </div>}
    {!loading && !state.error && !slug && listAllowed && <>
      {state.posts.length === 0 && <Typography>公開中の記事はありません。</Typography>}
      <ul className={classes.list} aria-label="公開記事">
        {state.posts.map((post) => <li key={post.slug}><article className={classes.row}>
          {post.cover_image_url && <Link className={classes.coverLink} to={articleHref(post)} aria-label={`${post.title}を読む`}>
            <img src={post.cover_image_url} alt={`${post.title}の代表画像`} className={classes.thumbnail} loading="lazy" />
          </Link>}
          <div className={classes.rowText}>
            <PostAuthor post={post} searchParams={searchParams} />
            <PostDates post={post} />
            <Typography component="h2" variant="h5"><Link className={classes.titleLink} to={articleHref(post)}>{post.title}</Link></Typography>
            <PostTags post={post} searchParams={searchParams} />
            <BlogShareActions slug={post.slug} title={post.title}
              leadingAction={<div className={classes.reactions}>
                <BlogLikeButton slug={post.slug} articleTitle={post.title} isAuthenticated={isAuthenticated} authScope={authScope} compact />
                <span className={classes.commentSummary} role="group" aria-label={`${post.title}：コメント ${post.comment_count}件`}>
                  <ChatBubbleOutlineIcon className={classes.commentIcon} aria-hidden="true" focusable="false" /><span>{post.comment_count}</span>
                </span>
              </div>} />
            <Typography className={classes.excerpt}>{listPreview(post)}</Typography>
            <div className={`${classes.actions} ${classes.readActions}`}><Button className={classes.readAction} component={Link} role="link" to={articleHref(post)}>記事を読む</Button></div>
          </div>
        </article></li>)}
      </ul>
      <BlogPagination page={currentPage} totalPages={state.pagination?.total_pages} onPageChange={changePage} />
    </>}
    {state.post && <BlogPostArticle post={state.post} searchParams={searchParams}
      beforeBody={<BlogShareActions slug={state.post.slug} title={state.post.title} />}
      afterBody={<BlogLikeButton slug={state.post.slug} isAuthenticated={isAuthenticated} authScope={authScope} />} />}
    {state.post && <BlogComments slug={state.post.slug} isAuthenticated={isAuthenticated} authScope={authScope} />}
    {state.post && listAllowed && <AdjacentPosts slug={state.post.slug} returnQuery={returnQuery} authScope={authScope} isAuthenticated={isAuthenticated} filters={filters} />}
    {state.post && <RelatedPosts slug={state.post.slug} returnQuery={returnQuery} authScope={authScope} />}
    {slug && <div className={classes.actions}>
      <Link className={classes.detailReturn} to={`/blog${returnQuery}`}><ArrowBackIcon className={classes.returnIcon} aria-hidden="true" focusable="false" />記事一覧へ</Link>
    </div>}
  </>;
}

export default function BlogPublicPage() {
  const { slug } = useParams();
  const auth = useContext(AuthContext) || {};
  const account = auth.user?.id || auth.user?.uuid || auth.user?.email || '';
  const authScope = `${Boolean(auth.isAuth)}:${account}`;
  return <BlogLayout><PublicContent key={slug || 'list'} slug={slug} authScope={authScope} isAuthenticated={Boolean(auth.isAuth)} /></BlogLayout>;
}
